import React, { useEffect, useState } from 'react';
import AuthAnimatedModal from 'components/shared/modal/AuthAnimatedModal';
import SchoolFeesPreviewModal from 'components/shared/modal/SchoolFeesPreviewModal';
import { RoutePaths } from 'routes/route-paths';
import { useNavigate } from 'react-router-dom';
import { Loader } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch } from 'store/hooks';
import { clearForm } from 'store/slices/houseRent';
import { appAxios } from 'api/axios';
import { sendCatchFeedback } from 'functions/feedback';
import Step3 from 'pages/home/rent/Step3';
import { Step2 } from 'pages/home/rent/Step2';
import Step4 from 'pages/home/rent/Step4';
import { formatToNaira } from 'functions/currencyConveter';
import { getTokenDetails } from 'functions/userSession';
import { decodeToken } from 'react-jwt';
import { TokenType } from 'types/jwt';

interface FormData {
  FirstName: string;
  LastName: string;
  Email: string;
  ContactAddress: string;
  City: string;
  State: string;
  PostalCode: string;
  Country: string;
  PhoneNumber: string;
  UtilityBill: File | null;
  RentPrice: string;
  RequestedAmount: string;
  IDCard: File | null;
  BankStatement: File | null;
  BankStatement2: File | null;
  BankStatement3: File | null;
  TenancyAgreement: File | null;
  PaySlip: File | null;
  TermsCondition: boolean;
  LandlordName: string;
  LandlordEmail: string;
  houseType: string;
  LandlordAccountName: string;
  LandlordAccountNumber: string;
  LandlordBankName: string;
  LandlordContactNumber: string;
  Occupation: string;
  CompanyName: string;
  YearsOfWork: string;
  CompanyEmail: string;
  CompanyPhoneNumber: string;
  CompanyLocation: string;
}

const steps = [
  { id: 1, label: 'Rent Details' },
  { id: 2, label: 'Occupation/Business Details' },
  { id: 3, label: 'Documents' },
];

const UserHouseRentPage = () => {
  const navigate = useNavigate();
  const [idempotencyKey, setIdempotencyKey] = useState(uuidv4());

  const [loading, setLoading] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [inputData, setInputData] = useState<FormData>({} as FormData);
  const [CustomerId, setCustomerId] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const dispatch = useAppDispatch();
  const handleInputDataUpdate = (data: FormData) => {
    setInputData((prevData) => ({ ...prevData, ...data }));
  };
  const handleProceed = () => {
    setIsAuthModalOpen(false);
    navigate(RoutePaths.USER_DASHBOARD);
  };
  const getClientId = () => {
    const token = getTokenDetails();
    if (!token) {
      return;
    }
    const decodedToken = decodeToken<TokenType>(token);
    if (decodedToken) {
      setCustomerId(decodedToken?.customerId);
    }
  };

  useEffect(() => {
    getClientId();
  }, []);
  const handlePreviewConfirm = async () => {
    try {
      setLoading(true);

      const formDataToSubmit = new FormData();
      const rentPriceWithoutCommas = String(inputData?.RentPrice).replace(
        /,/g,
        ''
      );
      const requestedAmountWithoutCommas = String(
        inputData?.RequestedAmount
      ).replace(/,/g, '');

      formDataToSubmit.append('IdempotencyKey', idempotencyKey);

      formDataToSubmit.append('RentPrice', rentPriceWithoutCommas);
      formDataToSubmit.append('RequestedAmount', requestedAmountWithoutCommas);
      formDataToSubmit.append(
        'LandLordRequest.LandLordName',
        inputData?.LandlordName || ''
      );
      formDataToSubmit.append('houseType', inputData?.houseType || '');
      if (inputData?.LandlordEmail) {
        formDataToSubmit.append(
          'LandLordRequest.LandLordEmail',
          inputData?.LandlordEmail || ''
        );
      }
      formDataToSubmit.append(
        'LandLordRequest.AccountName',
        inputData?.LandlordAccountName || ''
      );
      formDataToSubmit.append(
        'LandLordRequest.AccountNumber',
        inputData?.LandlordAccountNumber || ''
      );
      formDataToSubmit.append(
        'LandLordRequest.BankName',
        inputData?.LandlordBankName || ''
      );
      formDataToSubmit.append(
        'LandLordRequest.ContactNumber',
        inputData?.LandlordContactNumber || ''
      );
      formDataToSubmit.append(
        'RentEmployeerRequest.Occupation',
        inputData?.Occupation || ''
      );
      formDataToSubmit.append(
        'RentEmployeerRequest.CompanyName',
        inputData?.CompanyName || ''
      );

      formDataToSubmit.append(
        'RentEmployeerRequest.YearsOfWork',
        inputData?.YearsOfWork || ''
      );
      formDataToSubmit.append(
        'RentEmployeerRequest.CompanyEmail',
        inputData?.CompanyEmail || ''
      );
      formDataToSubmit.append(
        'RentEmployeerRequest.CompanyPhoneNumber',
        inputData?.CompanyPhoneNumber || ''
      );
      formDataToSubmit.append('CustomerId', CustomerId);
      formDataToSubmit.append(
        'RentEmployeerRequest.CompanyLocation',
        inputData?.CompanyLocation || ''
      );

      if (inputData?.BankStatement) {
        formDataToSubmit.append(
          'BankStatement',
          inputData.BankStatement,
          inputData.BankStatement.name
        );
      }
      if (inputData?.BankStatement2) {
        formDataToSubmit.append(
          'BankStatement2',
          inputData?.BankStatement2,
          inputData?.BankStatement2.name
        );
      }
      if (inputData?.BankStatement3) {
        formDataToSubmit.append(
          'BankStatement3',
          inputData?.BankStatement3,
          inputData?.BankStatement3.name
        );
      }
      if (inputData?.UtilityBill) {
        formDataToSubmit.append(
          'UtilityBill',
          inputData.UtilityBill,
          inputData.UtilityBill.name
        );
      }
      if (inputData?.IDCard) {
        formDataToSubmit.append(
          'IDCard',
          inputData.IDCard,
          inputData.IDCard.name
        );
      }
      if (inputData?.TenancyAgreement) {
        formDataToSubmit.append(
          'TenancyAgreement',
          inputData.TenancyAgreement,
          inputData.TenancyAgreement.name
        );
      }

      if (inputData?.PaySlip) {
        formDataToSubmit.append(
          'PaySlip',
          inputData.PaySlip,
          inputData.PaySlip.name
        );
      }

      // API call to submit the form data
      const response = await appAxios.post('/house-rent', formDataToSubmit, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setLoading(false);
        setIsPreviewModalOpen(false);
        setIsAuthModalOpen(true);
        setIdempotencyKey(uuidv4());
        setInputData({} as FormData);
        dispatch(clearForm());
      } else {
        setLoading(false);
        sendCatchFeedback(response.data);
      }
    } catch (error) {
      setLoading(false);
      sendCatchFeedback(error);
    }
  };

  const rentAmount = inputData.RentPrice
    ? parseFloat(inputData.RentPrice.toString().replace(/,/g, ''))
    : 0;

  const requestedAmount = inputData.RequestedAmount
    ? parseFloat(inputData.RequestedAmount.toString().replace(/,/g, ''))
    : 0;

  const getPreviewContent = () => {
    return (
      <div>
        <>
          <h4 className="mb-1 font-semibold text-left">
            House Rent Information
          </h4>
          <div className="grid grid-cols-1 gap-4 mb-3 text-left sm:grid-cols-2">
            <div>
              <p>
                <strong>Rent Amount:</strong> {formatToNaira(rentAmount) || 0}
              </p>
              <p>
                <strong>Requested Amount:</strong>{' '}
                {formatToNaira(requestedAmount) || 0}
              </p>
            </div>
            <div>
              <p>
                <strong>Landlord Name:</strong> {inputData?.LandlordName}
              </p>
            </div>

            <div>
              <p>
                <strong>Landlord Account Name:</strong>{' '}
                {inputData?.LandlordAccountName}
              </p>
            </div>

            <div>
              <p>
                <strong>Landlord Account Number:</strong>{' '}
                {inputData?.LandlordAccountNumber}
              </p>
            </div>

            <div>
              <p>
                <strong>Landlord Bank Name:</strong>{' '}
                {inputData?.LandlordBankName}
              </p>
            </div>

            <div>
              <p>
                <strong>Landlord Contact Number:</strong>{' '}
                {inputData?.LandlordContactNumber}
              </p>
            </div>

            <div>
              <p>
                <strong>Occupation:</strong> {inputData?.Occupation}
              </p>
            </div>

            <div>
              <p>
                <strong>Company Name:</strong> {inputData?.CompanyName}
              </p>
            </div>

            <div>
              <p>
                <strong>Company Phone:</strong> {inputData?.CompanyPhoneNumber}
              </p>
            </div>

            <div>
              <p>
                <strong>Years of Work:</strong> {inputData?.YearsOfWork}
              </p>
            </div>

            <div>
              <p>
                <strong>Company Email:</strong> {inputData?.CompanyEmail}
              </p>
            </div>

            <div>
              <p>
                <strong>Company Phone Number:</strong>{' '}
                {inputData?.CompanyPhoneNumber}
              </p>
            </div>

            <div>
              <p>
                <strong>Company Location:</strong> {inputData?.CompanyLocation}
              </p>
            </div>
          </div>
        </>
      </div>
    );
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Render the appropriate form step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step2
            prevStep={prevStep}
            nextStep={() => setCurrentStep(currentStep + 1)}
            handleInputDataUpdate={handleInputDataUpdate}
          />
        );
      case 2:
        return (
          <Step3
            prevStep={prevStep}
            handleInputDataUpdate={handleInputDataUpdate}
            nextStep={() => setCurrentStep(currentStep + 1)}
          />
        );
      case 3:
        return (
          <Step4
            handleInputDataUpdate={handleInputDataUpdate}
            prevStep={prevStep}
            nextStep={() => setIsPreviewModalOpen(true)}
          />
        );
      default:
        return null;
    }
  };

  // Render the step buttons (next, previous, submit)

  return (
    <div className="block px-4  py-4 sm:flex bg-BrandBg3">
      <div className="flex flex-col-reverse gap-7 w-full sm:flex-row">
        <div className="flex-1 p-8 bg-white rounded-md h-full shadow-card">
          <div className="mb-5">
            <h1 className="text-2xl fomt-semibold">House Rent Information</h1>
            <p className="text-lg text-gray-2">Complete the fields below</p>
          </div>

          {/* Progress indicator */}
          <div className="my-6">
            <div className="flex justify-between">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full ${
                        currentStep >= step.id
                          ? 'bg-BrandPrimary text-white'
                          : 'bg-gray-200'
                      }`}
                    >
                      {step.id}
                    </div>
                    <span className="mt-1 text-xs">{step.label}</span>
                  </div>

                  {/* Add connector line except after the last step */}
                  {index < steps.length - 1 && (
                    <div className="flex-1 mx-2 mt-4 h-1 bg-gray-200">
                      <div
                        className={`h-full ${
                          currentStep > step.id
                            ? 'bg-BrandPrimary'
                            : 'bg-gray-200'
                        }`}
                      ></div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {renderStepContent()}
        </div>
      </div>

      <AuthAnimatedModal
        isOpen={isAuthModalOpen}
        onClose={handleProceed}
        onConfirm={handleProceed}
        title="House rent Form Submitted!"
        btnText="Proceed"
        content={
          <p>
            You have successfully submitted your house rent form, Kindly check
            your email to complete the process.
          </p>
        }
      />

      <SchoolFeesPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        title="Preview Your Submission"
        btnText={loading ? <Loader className="animate-spin" /> : 'Submit'}
        content={getPreviewContent()}
        onConfirm={handlePreviewConfirm}
      />
    </div>
  );
};

export default UserHouseRentPage;
